// src/pages/B2B/event-reports/api/useGenerateReport.ts
import { useCallback, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import type { Env } from '@/pages/Admin/shared';
import { useAdminApi } from '@/pages/Admin/shared/adminApi';
import { buildCsv } from '../csv/buildCsv';
import { buildPdfBlob } from '../pdf/ReportPdfDocument';
import type {
  ActiveUsersSample,
  BuilderFormState,
  EventReportPayload,
  EventReportRecord,
} from '../types';

async function blobToBase64(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  let binary = '';
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

const MAX_SAMPLES = 1500;

// Samples are one per minute, so a 31-day window is ~45k points, sent twice
// (overall + spotlight). That stalls the chart render and pushes the create
// request toward the Lambda payload limit.
// ponytail: keeps each bucket's peak sample, so the saved payload is not
// minute-level beyond ~25h windows. Store the raw series server-side if that
// is ever needed.
function thinSamples(samples: ActiveUsersSample[]): ActiveUsersSample[] {
  if (samples.length <= MAX_SAMPLES) return samples;
  const size = Math.ceil(samples.length / MAX_SAMPLES);
  const out: ActiveUsersSample[] = [];
  for (let i = 0; i < samples.length; i += size) {
    let peak = samples[i];
    const end = Math.min(i + size, samples.length);
    for (let j = i + 1; j < end; j++) {
      if (samples[j].count > peak.count) peak = samples[j];
    }
    out.push(peak);
  }
  return out;
}

export function useGenerateReport(env: Env) {
  const { request } = useAdminApi();
  const { accounts } = useMsal();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generate = useCallback(
    async (form: BuilderFormState): Promise<EventReportRecord | null> => {
      setLoading(true);
      setError(null);
      try {
        const raw = await request<EventReportPayload>({
          env,
          path: '/admin/event-reports/aggregate',
          method: 'POST',
          body: {
            from_local: form.from_local,
            to_local: form.to_local,
            timezone: form.timezone,
            spotlight_business_id: form.spotlight_mode === 'manual' ? form.spotlight_business_id : null,
            spotlight_auto: form.spotlight_mode === 'auto',
          },
        });
        const payload: EventReportPayload = {
          ...raw,
          active_users_samples: thinSamples(raw.active_users_samples),
          spotlight_venue: raw.spotlight_venue && {
            ...raw.spotlight_venue,
            active_users_samples: thinSamples(raw.spotlight_venue.active_users_samples),
          },
        };

        const csv = buildCsv({
          payload,
          eventName: form.event_name,
          fromLocal: form.from_local,
          toLocal: form.to_local,
          timezone: form.timezone,
          generatedAt: new Date().toISOString(),
          generatedBy: accounts[0]?.username ?? 'unknown',
        });

        const pdfBlob = await buildPdfBlob({
          reportType: form.report_type,
          payload,
          eventName: form.event_name,
          fromLocal: form.from_local,
          toLocal: form.to_local,
          timezone: form.timezone,
          locationLabel: form.location_label,
          imageDataUrl: form.image_preview_url,
        });

        return await request<EventReportRecord>({
          env,
          path: '/admin/event-reports',
          method: 'POST',
          body: {
            event_name: form.event_name,
            from_local: form.from_local,
            to_local: form.to_local,
            timezone: form.timezone,
            location_label: form.location_label || null,
            image_staging_key: form.image_staging_key,
            payload,
            csv_b64: btoa(unescape(encodeURIComponent(csv))),
            pdf_b64: await blobToBase64(pdfBlob),
            report_type: form.report_type,
          },
        });
      } catch (e: any) {
        setError(e?.message ?? 'Generation failed');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [request, env, accounts],
  );

  return { generate, loading, error };
}
